import { toast } from 'sonner';
import { loadSoundSettings } from '@/lib/notification-sound-settings';
import { formatMoney } from '@/lib/money';
import { posProvidersClient } from '@/lib/pos-providers-client';
import i18n from '@/i18n/config';
import type { NotificationPayload } from '@/types/notifications';

const NOTIFICATION_SOUND_URL = '/sounds/universfield-new-notification-020-352772.mp3';
let notificationAudio: HTMLAudioElement | null = null;
let audioUnlocked = false;

function ensureAudio(): HTMLAudioElement | null {
  if (typeof window === 'undefined') return null;
  if (!notificationAudio) {
    notificationAudio = new Audio(NOTIFICATION_SOUND_URL);
    notificationAudio.volume = 0.6;
    notificationAudio.preload = 'auto';
  }
  return notificationAudio;
}

/**
 * Browsers block `Audio.play()` until the user has interacted with the page, and a notification
 * fired from the SSE stream never happens inside a user gesture — so the sound was silently
 * blocked forever. Prime the element on the FIRST pointer/key interaction (play it muted, then
 * reset) so later, event-driven plays are allowed. Call once, early.
 */
export function initNotificationAudioUnlock() {
  if (typeof window === 'undefined' || audioUnlocked) return;
  const unlock = () => {
    const audio = ensureAudio();
    if (!audio) return;
    audio.muted = true;
    audio
      .play()
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.muted = false;
        audioUnlocked = true;
        window.removeEventListener('pointerdown', unlock);
        window.removeEventListener('keydown', unlock);
      })
      .catch(() => {
        audio.muted = false;
      });
  };
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);
}

let lastPlayedAt = 0;
let ringSession = 0;

/** One chime, resolved when it finishes (or immediately if playback is unavailable). */
function playOnce(audio: HTMLAudioElement): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      audio.removeEventListener('ended', done);
      resolve();
    };
    audio.addEventListener('ended', done);
    try {
      audio.currentTime = 0;
      // Should be unlocked by initNotificationAudioUnlock(); swallow the rejection just in case
      // (e.g. the user hasn't interacted yet) so the visual notification still fires.
      void audio.play().catch(done);
    } catch {
      done();
    }
  });
}

/**
 * Ring the notification chime according to the operator's sound settings (enabled, number of
 * repeats, volume — see notification-sound-settings.ts). Exported so the conversation list can
 * ring it directly when it detects a handoff via its 2s poll — the poll is the delivery path we
 * KNOW reaches this operator (it's what paints the row red), independent of the SSE stream's
 * client-targeting. A short dedupe window keeps the SSE handler and the poll from double-ringing
 * the same event; a new event supersedes any repeat sequence still ringing.
 *
 * `force` (the settings panel's "test" button) bypasses the enabled check and the dedupe window.
 */
export function playNotificationSound(force = false) {
  const settings = loadSoundSettings();
  if (!settings.enabled && !force) return;
  const audio = ensureAudio();
  if (!audio) return;
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  if (!force && now - lastPlayedAt < 3000) return;
  lastPlayedAt = now;
  audio.volume = settings.volume;

  const session = ++ringSession;
  void (async () => {
    for (let i = 0; i < settings.repeats; i++) {
      if (session !== ringSession) return; // a newer notification took over the speaker
      await playOnce(audio);
      if (i < settings.repeats - 1) await new Promise((r) => setTimeout(r, 350));
    }
  })();
}

let persistentTimer: ReturnType<typeof setInterval> | null = null;

/** How long between bursts while conversations are still waiting for a human. */
const PERSISTENT_RING_INTERVAL_MS = 15_000;

/**
 * Keep ringing until someone attends the queue — the "suena hasta que una persona intervenga"
 * mode. Rings immediately, then every {@link PERSISTENT_RING_INTERVAL_MS} until
 * {@link stopPersistentRing} is called (the caller does that the moment the last pending handoff
 * clears). Safe to call repeatedly: while a cycle is already running this is a no-op, so the
 * 2-second inbox poll driving it never stacks timers or shortens the gap.
 */
export function startPersistentRing() {
  if (persistentTimer !== null) return;
  if (!loadSoundSettings().enabled) return;
  playNotificationSound(true);
  persistentTimer = setInterval(() => {
    // Re-read each cycle so switching the mode off in the panel silences it without a reload.
    const settings = loadSoundSettings();
    if (!settings.enabled || !settings.untilAttended) {
      stopPersistentRing();
      return;
    }
    playNotificationSound(true);
  }, PERSISTENT_RING_INTERVAL_MS);
}

export function stopPersistentRing() {
  if (persistentTimer === null) return;
  clearInterval(persistentTimer);
  persistentTimer = null;
}

export async function ensureNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

function destinationFor(payload: NotificationPayload): string | null {
  // Straight to the board, not to "/": "/" is the landing decision, which for a manual-WhatsApp
  // brand is the inbox — and a new order is board work whatever the landing is.
  if (payload.type === 'new_order') return '/board';
  // A rider problem, or a transfer-held cart that needs a human: the order it is about when there is
  // one (the board opens its detail, carts included), else the board itself, where a "Solo envío"
  // sits in Atención.
  if (payload.type === 'delivery_alert' || payload.type === 'transfer_order_needs_review') {
    return payload.data?.orderId ? `/board?orderId=${encodeURIComponent(payload.data.orderId)}` : '/board';
  }
  if (payload.type === 'conversation_switched_to_manual' || payload.type === 'conversation_needs_intervention') {
    return payload.data?.conversationId
      ? `/conversations?conversationId=${payload.data.conversationId}`
      : '/conversations';
  }
  return null;
}

/**
 * An aggregate session receives events from every brand in the subtree, and "Nuevo pedido" alone
 * says nothing about which one — so the brand leads the title. Line sessions get the title as is.
 */
export function formatNotificationTitle(payload: NotificationPayload): string {
  if (!payload.brandName) return payload.title;
  return i18n.t('app:notifications.brandPrefix', { brand: payload.brandName, title: payload.title });
}

/**
 * The alert's own copy. `transfer_order_needs_review` carries no title or body in its contract (only
 * the draft's figures), so it is written here, in the operator's language; every other event brings
 * its copy from the backend.
 */
function copyFor(payload: NotificationPayload): { title: string; body: string } {
  if (payload.type !== 'transfer_order_needs_review') return { title: payload.title, body: payload.body };
  const data = payload.data ?? {};
  const params = {
    id: data.orderId?.slice(0, 8) ?? '',
    total: typeof data.total === 'number' ? formatMoney(data.total, data.currency, i18n.language) : '',
    customer: data.customerName ?? '',
  };
  return {
    title: i18n.t('app:notifications.transferReviewTitle'),
    body: data.customerName
      ? i18n.t('app:notifications.transferReviewBodyNamed', params)
      : i18n.t('app:notifications.transferReviewBody', params),
  };
}

/** How long a brand's "has a POS" answer is trusted before asking again. */
const POS_LOOKUP_TTL_MS = 5 * 60_000;
const posLookups = new Map<string, { hasPos: Promise<boolean>; at: number }>();

/**
 * Whether the brand pushes its orders to a POS (any enabled provider). Cached per brand, since an
 * aggregate session receives orders from many brands and each event would otherwise re-ask. A
 * failed lookup is not cached, so the next order retries it.
 */
function brandHasPos(brandId: string): Promise<boolean> {
  const cached = posLookups.get(brandId);
  if (cached && Date.now() - cached.at < POS_LOOKUP_TTL_MS) return cached.hasPos;
  const hasPos = posProvidersClient.getProviders(brandId).then((providers) => providers.some((p) => p.enabled));
  posLookups.set(brandId, { hasPos, at: Date.now() });
  hasPos.catch(() => posLookups.delete(brandId));
  return hasPos;
}

/** Orders already alerted on this tab, so a later event for the same pending order stays quiet. */
const alertedPendingOrderIds = new Set<string>();
const MAX_ALERTED_ORDER_IDS = 500;

/**
 * A new order waiting for the operator — the order-side twin of the handoff alert. Only for
 * Menteo-only brands: with a POS the order goes straight to the restaurant's system, so there is
 * nothing for the operator to confirm here. Read off the board's `order.board.changed` feed (the
 * backend has no dedicated new-order event), entering `pending` being the "placed" moment.
 */
async function alertIfNewPendingOrder(payload: NotificationPayload) {
  const orderId = payload.data?.orderId;
  if (payload.data?.status !== 'pending' || !orderId || !payload.brandId) return;
  if (alertedPendingOrderIds.has(orderId)) return;
  alertedPendingOrderIds.add(orderId);
  if (alertedPendingOrderIds.size > MAX_ALERTED_ORDER_IDS) {
    const oldest = alertedPendingOrderIds.values().next().value;
    if (oldest) alertedPendingOrderIds.delete(oldest);
  }

  let hasPos: boolean;
  try {
    hasPos = await brandHasPos(payload.brandId);
  } catch {
    // Unknown: stay quiet rather than ring for a POS brand. The board and its badge still show it.
    return;
  }
  if (hasPos) return;

  showNotification({
    ...payload,
    type: 'new_order',
    title: i18n.t('app:notifications.newOrderTitle'),
    body: i18n.t('app:notifications.newOrderBody', { id: orderId.slice(0, 8) }),
  });
}

export function showNotification(payload: NotificationPayload) {
  // `order.board.changed` is the board's data feed, not an operator alert: every status change of
  // every order would ring the chime and stack a toast. The board subscribes to it separately; the
  // one change that IS an alert — a new pending order on a Menteo-only brand — is picked out here.
  if (payload.type === 'order.board.changed') {
    void alertIfNewPendingOrder(payload);
    return;
  }
  // Same for standalone deliveries: a data event for the board, never a chime. The failures that need
  // a human arrive as operator alerts of their own (spec §4.4).
  if (payload.type === 'delivery_changed') return;

  const destination = destinationFor(payload);
  const copy = copyFor(payload);
  const title = formatNotificationTitle({ ...payload, title: copy.title });

  playNotificationSound();

  toast(title, {
    description: copy.body,
    ...(destination
      ? {
          action: {
            label: i18n.t('app:notifications.view'),
            onClick: () => {
              window.location.href = destination;
            },
          },
        }
      : {}),
  });

  if (document.visibilityState === 'visible' || Notification.permission !== 'granted') return;

  const n = new Notification(title, {
    body: copy.body,
    // Same tag = the browser replaces the previous banner. Keyed by brand too, so two brands'
    // handoffs no longer overwrite each other in an aggregate session.
    // A rider alert is about one dispatch, a transfer review about one cart: two of them must not
    // replace each other.
    tag:
      payload.type === 'delivery_alert' && payload.data?.dispatchId
        ? `${payload.type}:${payload.data.dispatchId}`
        : payload.type === 'transfer_order_needs_review' && payload.data?.orderId
          ? `${payload.type}:${payload.data.orderId}`
          : payload.brandId
            ? `${payload.type}:${payload.brandId}`
            : payload.type,
  });
  n.onclick = () => {
    window.focus();
    if (destination) {
      window.location.href = destination;
    }
    n.close();
  };
}
